import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type IErrorData } from '@/utils/getErrorData';
import { type IOrderRecipient } from '@/types/IOrder';
import {
  type DeliveryInfo,
  initialDeliveryInfo
} from '@/types/FunctionalModal';
import { updateRecipientAndDeliveryThunk } from './recipientEditThunks';

export interface IEditedRecipientValues {
  phone: string;
  email: string;
  address: string;
  name: string;
}
export interface IRecipientDisplayData {
  name: string;
  phone: string;
}

export interface IModalValidationErrors {
  name: boolean;
  phone: boolean;
  email: boolean;
}

interface RecipientEditState {
  showModal: boolean;
  editedValues: IEditedRecipientValues;
  recipientDisplayData: IRecipientDisplayData;
  deliveryInfo: DeliveryInfo;
  dataFromServer: IOrderRecipient | null;
  modalValidationErrors: IModalValidationErrors;
  isUpdating: boolean;
  error: IErrorData | null;
}

const initialModalValidationErrors: IModalValidationErrors = {
  name: false,
  phone: false,
  email: false
};

const initialEditedValues: IEditedRecipientValues = {
  phone: '',
  email: '',
  address: '',
  name: ''
};

const initialRecipientDisplayData: IRecipientDisplayData = {
  name: '',
  phone: ''
};

const initialState: RecipientEditState = {
  showModal: false,
  editedValues: initialEditedValues,
  recipientDisplayData: initialRecipientDisplayData,
  deliveryInfo: initialDeliveryInfo,
  dataFromServer: null,
  modalValidationErrors: initialModalValidationErrors,
  isUpdating: false,
  error: null
};

const recipientEditSlice = createSlice({
  name: 'recipientEdit',
  initialState,
  reducers: {
    initializeRecipientData(
      state,
      action: PayloadAction<{
        orderRecipient: IOrderRecipient;
      }>
    ) {
      const { orderRecipient } = action.payload;

      state.editedValues = {
        phone: orderRecipient.phone,
        email: orderRecipient.email ?? '',
        address: orderRecipient.orderRecipientAddress?.address ?? '',
        name: orderRecipient.name
      };

      state.recipientDisplayData = {
        name: orderRecipient.name,
        phone: orderRecipient.phone
      };

      state.deliveryInfo = initialDeliveryInfo;
      state.dataFromServer = null;
      state.error = null;
    },

    openEditModal(state, action: PayloadAction<{ address?: string }>) {
      const { address } = action.payload;

      if (address !== undefined) {
        state.editedValues.address = address;
      }

      state.showModal = true;
      state.modalValidationErrors = initialModalValidationErrors;
    },

    closeEditModal(
      state,
      action: PayloadAction<{
        orderRecipient: IOrderRecipient;
      }>
    ) {
      const { orderRecipient } = action.payload;

      state.showModal = false;
      state.editedValues = {
        phone: orderRecipient.phone,
        email: orderRecipient.email ?? '',
        address: orderRecipient.orderRecipientAddress?.address ?? '',
        name: orderRecipient.name
      };
      state.deliveryInfo = initialDeliveryInfo;
      state.modalValidationErrors = initialModalValidationErrors;
    },

    updateEditedValues(
      state,
      action: PayloadAction<Partial<IEditedRecipientValues>>
    ) {
      state.editedValues = {
        ...state.editedValues,
        ...action.payload
      };
    },

    updateDeliveryInfo(state, action: PayloadAction<Partial<DeliveryInfo>>) {
      state.deliveryInfo = {
        ...state.deliveryInfo,
        ...action.payload
      };
    },

    setDeliveryInfo(state, action: PayloadAction<DeliveryInfo>) {
      state.deliveryInfo = action.payload;
    },

    updateModalValidationErrors(
      state,
      action: PayloadAction<Partial<IModalValidationErrors>>
    ) {
      state.modalValidationErrors = {
        ...state.modalValidationErrors,
        ...action.payload
      };
    },

    resetModalValidationErrors(state) {
      state.modalValidationErrors = initialModalValidationErrors;
    },

    clearError(state) {
      state.error = null;
    },

    resetRecipientEditState(state) {
      Object.assign(state, initialState);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateRecipientAndDeliveryThunk.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateRecipientAndDeliveryThunk.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.showModal = false;
        state.dataFromServer = action.payload.orderRecipient;
        state.recipientDisplayData = {
          name: action.payload.orderRecipient.name ?? '',
          phone: action.payload.orderRecipient.phone ?? ''
        };
        state.editedValues = {
          phone: action.payload.orderRecipient.phone,
          email: action.payload.orderRecipient.email ?? '',
          address:
            action.payload.orderRecipient.orderRecipientAddress?.address ?? '',
          name: action.payload.orderRecipient.name
        };
        state.deliveryInfo = initialDeliveryInfo;
        state.modalValidationErrors = initialModalValidationErrors;
      })
      .addCase(updateRecipientAndDeliveryThunk.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? null;
      });
  }
});

export const {
  initializeRecipientData,
  openEditModal,
  closeEditModal,
  updateEditedValues,
  updateDeliveryInfo,
  setDeliveryInfo,
  updateModalValidationErrors,
  resetModalValidationErrors,
  clearError,
  resetRecipientEditState
} = recipientEditSlice.actions;

export const selectRecipientEditState = (state: {
  recipientEdit: RecipientEditState;
}) => state.recipientEdit;
export const selectShowModal = (state: { recipientEdit: RecipientEditState }) =>
  state.recipientEdit.showModal;
export const selectEditedValues = (state: {
  recipientEdit: RecipientEditState;
}) => state.recipientEdit.editedValues;
export const selectRecipientDisplayData = (state: {
  recipientEdit: RecipientEditState;
}) => state.recipientEdit.recipientDisplayData;
export const selectDeliveryInfo = (state: {
  recipientEdit: RecipientEditState;
}) => state.recipientEdit.deliveryInfo;
export const selectDataFromServer = (state: {
  recipientEdit: RecipientEditState;
}) => state.recipientEdit.dataFromServer;
export const selectModalValidationErrors = (state: {
  recipientEdit: RecipientEditState;
}) => state.recipientEdit.modalValidationErrors;
export const selectIsUpdating = (state: {
  recipientEdit: RecipientEditState;
}) => state.recipientEdit.isUpdating;
export const selectError = (state: { recipientEdit: RecipientEditState }) =>
  state.recipientEdit.error;

export default recipientEditSlice.reducer;
